import { ApiError, getApiErrorMessage } from './hub-api-request';
import useApiCallable from './use-api-callable';

type ConstraintViolation = {
  propertyPath: string;
  message: string;
};

type ConstraintViolationListResponse = {
  '@type': 'ConstraintViolationList';
  violations: ConstraintViolation[];
};

function isConstraintViolationList(
  data: Record<string, unknown>
): data is ConstraintViolationListResponse {
  return (
    data['@type'] === 'ConstraintViolationList' &&
    Array.isArray(data['violations'])
  );
}

type FormSubmitHelpers = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addSubmitError(field: any, error: string): void;
  reset(): void;
  setLoading(loading: boolean): void;
};

type ApiFormSubmitFn = (
  values: Record<string, unknown>,
  form: FormSubmitHelpers
) => Promise<boolean>;

export default function useApiFormSubmit(
  method: 'PUT' | 'POST' | 'PATCH',
  path: string,
  extras: {
    setCreated?: (arg1?: unknown) => unknown;
    setEditing?: (arg1: boolean) => unknown;
    onSuccess?: () => unknown;
    invalidate?: () => unknown;
  } = {}
): ApiFormSubmitFn {
  const callApi = useApiCallable();

  return async function (
    values: Record<string, unknown>,
    { addSubmitError, reset, setLoading }: FormSubmitHelpers
  ): Promise<boolean> {
    setLoading(true);
    try {
      // Trim whitespace from any string values in the form.  We're assuming
      // any leading/trailing spaces on typical forms (those submitted with
      // useApiFormSubmit) are always entered by mistake.
      const transformedValues = Object.fromEntries(
        Object.entries(values).map(([k, v]) => [
          k,
          typeof v === 'string' ? v.trim() : v,
        ])
      );
      const response = await callApi(path, {
        method,
        jsonBody: transformedValues,
      });
      setLoading(false);
      extras.setEditing?.(false);
      extras.setCreated?.(response.data);
      extras.onSuccess?.();

      // Optional invalidation behaviour: resets the values stored in the form
      // state, and fetches fresh data from the server.
      if (extras.invalidate) {
        reset();
        extras.invalidate();
      }

      return true;
    } catch (err: unknown) {
      const apiError = err instanceof ApiError ? err : null;
      const errData = (apiError?.response?.data ?? {}) as Record<
        string,
        unknown
      >;

      if (isConstraintViolationList(errData)) {
        errData.violations.forEach((violation) => {
          if (!addSubmitError) {
            throw new Error(
              'handleError: Got a ConstraintViolationList but no addSubmitError option'
            );
          }
          addSubmitError(violation.propertyPath, violation.message);
        });
      } else {
        // Not a field-level validation error — surface whatever the API told
        // us (API Platform usually sends `hydra:description` / `detail`)
        // instead of a meaningless "Unhandled error".
        alert(getApiErrorMessage(err));
        throw err;
      }

      setLoading(false);

      return false;
    }
  };
}
